import { IAnotacao, ICabecalho, IGrupoDePerguntas, IPergunta } from "../../interfaces/IResponderFormularioResponse"
import { ICargaAnotacao, ICargaCabecalho, ICargaGrupoDePerguntas, ICargaPergunta, ICargaRodape } from "../../interfaces/ICargaResponderFormulario"

export const montarCargaDeCabecalho = (cabecalho: ICabecalho[]): ICargaCabecalho[] => {
	return cabecalho.map(c => ({ id: c.id, titulo: c.titulo, valor: c.valor }))
};

export const montarCargaDeRodape = (rodape: ICabecalho[]): ICargaRodape[] => {
	return rodape.map(r => ({ id: r.id, titulo: r.titulo, valor: r.valor }))
};

const montarCargaDeAnotacoes = (anotacoes: IAnotacao[]): ICargaAnotacao[] => {
	return anotacoes.map(a => ({ id: a.id, dataHora: a.dataHora, mensagem: a.mensagem, usuario: a.usuario }))
}

const montarCargaDePerguntas = (perguntas: IPergunta[]): ICargaPergunta[] => {
	return perguntas.map(p => ({
		id: p.id,
		grupoPerguntaId: p.grupoPerguntaId,
		planoDeAcaoId: p.planoDeAcaoId,
		respostaSelecionada: p.respostaSelecionada,
		respostaSelecionadaId: p.respostaSelecionadaId,
		respondidoPor: p.respondidoPor,
		anotacoes: montarCargaDeAnotacoes(p.anotacoes),
		anotacao: p.anotacao,
		observacao: p.observacao,
		evidenciaAnexada: p.evidenciaAnexada,
		imagens: p.imagens,
		acoes: p.acoes
	}))
};

export const montarCargaDeGrupoDePerguntas = (grupoDePerguntas: IGrupoDePerguntas[]): ICargaGrupoDePerguntas[] => {
	return grupoDePerguntas.map(grupo => ({
		id: grupo.id,
		perguntas: montarCargaDePerguntas(grupo.perguntas),
		grupoDePerguntas: montarCargaDeGrupoDePerguntas(grupo.grupoDePerguntas),
	}))
};

// O formulário carregado é a referência da edição. Comparar apenas os campos
// persistidos evita que propriedades usadas somente pela interface gerem uma
// atualização concorrente indevida.
const normalizar = (valor: any): any => {
	if (valor === undefined || valor === null) return null;
	if (Array.isArray(valor)) return valor.map(normalizar);
	if (typeof valor !== 'object') return valor;

	return Object.keys(valor).sort().reduce((resultado, chave) => {
		resultado[chave] = normalizar(valor[chave]);
		return resultado;
	}, {} as any);
};

const valoresIguais = (primeiro: any, segundo: any): boolean =>
	JSON.stringify(normalizar(primeiro)) === JSON.stringify(normalizar(segundo));

const montarCargaDePergunta = (pergunta: IPergunta): ICargaPergunta => ({
	id: pergunta.id,
	grupoPerguntaId: pergunta.grupoPerguntaId,
	planoDeAcaoId: pergunta.planoDeAcaoId,
	respostaSelecionada: pergunta.respostaSelecionada,
	respostaSelecionadaId: pergunta.respostaSelecionadaId,
	respondidoPor: pergunta.respondidoPor,
	anotacoes: montarCargaDeAnotacoes(pergunta.anotacoes),
	anotacao: pergunta.anotacao,
	observacao: pergunta.observacao,
	evidenciaAnexada: pergunta.evidenciaAnexada,
	imagens: pergunta.imagens,
	acoes: pergunta.acoes,
});

const camposAlteradosDaPergunta = (atual: IPergunta, original?: IPergunta): string[] => {
	const cargaAtual = montarCargaDePergunta(atual);
	const cargaOriginal = original ? montarCargaDePergunta(original) : undefined;

	if (!cargaOriginal) {
		return ['resposta', 'observacao', 'anotacao', 'anotacoes', 'acoes', 'evidenciaAnexada', 'imagens'];
	}

	const camposAlterados: string[] = [];
	if (!valoresIguais(
		{ respostaSelecionada: cargaAtual.respostaSelecionada, respostaSelecionadaId: cargaAtual.respostaSelecionadaId },
		{ respostaSelecionada: cargaOriginal.respostaSelecionada, respostaSelecionadaId: cargaOriginal.respostaSelecionadaId })) camposAlterados.push('resposta');
	if (!valoresIguais(cargaAtual.observacao, cargaOriginal.observacao)) camposAlterados.push('observacao');
	if (!valoresIguais(cargaAtual.anotacao, cargaOriginal.anotacao)) camposAlterados.push('anotacao');
	if (!valoresIguais(cargaAtual.anotacoes, cargaOriginal.anotacoes)) camposAlterados.push('anotacoes');
	if (!valoresIguais(cargaAtual.acoes, cargaOriginal.acoes)) camposAlterados.push('acoes');
	if (!valoresIguais(cargaAtual.evidenciaAnexada, cargaOriginal.evidenciaAnexada)) camposAlterados.push('evidenciaAnexada');
	if (!valoresIguais(cargaAtual.imagens, cargaOriginal.imagens)) camposAlterados.push('imagens');
	return camposAlterados;
};

const indiceDePerguntas = (grupos: IGrupoDePerguntas[]): Map<number, IPergunta> => {
	const indice = new Map<number, IPergunta>();
	const adicionar = (itens: IGrupoDePerguntas[]) => itens.forEach(grupo => {
		(grupo.perguntas || []).forEach(pergunta => indice.set(pergunta.id, pergunta));
		adicionar(grupo.grupoDePerguntas || []);
	});
	adicionar(grupos || []);
	return indice;
};

/**
 * Cria o payload de sincronização completo. O backend usa camposAlterados
 * para decidir o que persistir, mas todos os itens continuam no contrato
 * para preservar os consumidores que dependem do retrato integral.
 */
export const montarCargaParcialDeGrupoDePerguntas = (
	grupoDePerguntas: IGrupoDePerguntas[],
	grupoDePerguntasOriginal: IGrupoDePerguntas[],
): ICargaGrupoDePerguntas[] => {
	const perguntasOriginais = indiceDePerguntas(grupoDePerguntasOriginal || []);
	const montar = (grupos: IGrupoDePerguntas[]): ICargaGrupoDePerguntas[] => grupos.map(grupo => {
		const perguntas = (grupo.perguntas || []).map(pergunta => {
			const camposAlterados = camposAlteradosDaPergunta(pergunta, perguntasOriginais.get(pergunta.id));
			return { ...montarCargaDePergunta(pergunta), camposAlterados };
		});

		return {
			id: grupo.id,
			perguntas,
			grupoDePerguntas: montar(grupo.grupoDePerguntas || []),
		};
	});

	return montar(grupoDePerguntas || []);
};

const montarCargaParcialDeCabecalhoInterna = <TCarga extends ICargaCabecalho>(
	atual: ICabecalho[],
	original: ICabecalho[],
): TCarga[] => {
	const originaisPorId = new Map((original || []).map(item => [item.id, item]));
	return (atual || []).map(item => {
		const originalDoItem = originaisPorId.get(item.id);
		const camposAlterados = !originalDoItem || !valoresIguais(item.valor, originalDoItem.valor)
			? ['valor']
			: [];

		return { id: item.id, titulo: item.titulo, valor: item.valor, camposAlterados } as TCarga;
	});
};

export const montarCargaParcialDeCabecalho = (atual: ICabecalho[], original: ICabecalho[]): ICargaCabecalho[] =>
	montarCargaParcialDeCabecalhoInterna<ICargaCabecalho>(atual, original);

export const montarCargaParcialDeRodape = (atual: ICabecalho[], original: ICabecalho[]): ICargaRodape[] =>
	montarCargaParcialDeCabecalhoInterna<ICargaRodape>(atual, original);
